// src/DuplicateQuestionsViewer.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, deleteDoc, doc, getDoc } from "firebase/firestore";
import { db, auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import Menu from "./Menu";

// String similarity functions (optimized with early exit)
// Helper to check if two strings can possibly be similar based on length
function canBeSimilar(length1, length2, threshold = 0.5) {
  if (length1 === 0 || length2 === 0) return false;
  const longer = Math.max(length1, length2);
  const shorter = Math.min(length1, length2);
  // For similarity > threshold, max edit distance = longer * (1 - threshold)
  // If length difference alone exceeds this, they can't be similar
  return shorter >= longer * threshold;
}

// Optimized editDistance with early exit
function editDistance(s1, s2, maxDistance = Infinity) {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();
  const costs = [];
  let minCost = Infinity;
  
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) costs[j] = j;
      else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1))
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        costs[j - 1] = lastValue;
        lastValue = newValue;
        minCost = Math.min(minCost, lastValue);
      }
    }
    if (i > 0) costs[s2.length] = lastValue;
    // Early exit if we've exceeded max distance
    if (minCost > maxDistance) return minCost;
  }
  return costs[s2.length];
}

function calculateSimilarity(s1, s2, threshold = 0.5) {
  let longer = s1;
  let shorter = s2;
  if (s1.length < s2.length) {
    longer = s2;
    shorter = s1;
  }
  let longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  
  // Early exit: check if lengths allow similarity
  if (!canBeSimilar(s1.length, s2.length, threshold)) {
    return 0;
  }
  
  // Calculate max allowed distance for early exit
  const maxDistance = Math.ceil(longerLength * (1 - threshold));
  const distance = editDistance(longer, shorter, maxDistance);
  
  // If distance exceeds threshold, return 0 early
  if (distance > maxDistance) return 0;
  
  return (longerLength - distance) / parseFloat(longerLength);
}

// Normalize question text for exact matching
function normalizeText(text) {
  if (!text || typeof text !== 'string') return '';
  return text.trim().toLowerCase();
}

export default function DuplicateQuestionsViewer() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [progress, setProgress] = useState({ phase: '', percentage: 0, current: 0, total: 0 });
  const [cancelled, setCancelled] = useState(false);
  const cancelRef = useRef(false);
  const navigate = useNavigate();
  const [isQwizard, setIsQwizard] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);

  // Results state
  const [results, setResults] = useState({
    totalQuestions: 0,
    uniqueQuestions: 0,
    exactDuplicateGroups: [],
    nearDuplicateGroups: [],
    totalExactDuplicates: 0,
    totalNearDuplicates: 0,
    totalExactDuplicateGroups: 0,
    totalNearDuplicateGroups: 0,
  });

  // Fetch and analyze duplicates
  const analyzeDuplicates = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCancelled(false);
    cancelRef.current = false;
    
    try {
      // Fetch all questions
      setProgress({ phase: 'Fetching questions...', percentage: 0, current: 0, total: 0 });
      const querySnapshot = await getDocs(collection(db, "QuestionLibrary"));
      const allQuestions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const totalQuestions = allQuestions.length;
      setProgress({ phase: 'Finding exact duplicates...', percentage: 10, current: 0, total: totalQuestions });

      // Exact duplicates detection
      const exactGroupsMap = new Map();
      allQuestions.forEach(q => {
        const normalized = normalizeText(q.question);
        if (normalized) {
          if (!exactGroupsMap.has(normalized)) {
            exactGroupsMap.set(normalized, []);
          }
          exactGroupsMap.get(normalized).push(q);
        }
      });

      const exactDuplicateGroups = [];
      let totalExactDuplicates = 0;
      exactGroupsMap.forEach((questions, normalizedText) => {
        if (questions.length > 1) {
          exactDuplicateGroups.push({
            normalizedText,
            count: questions.length,
            questions: questions.map(q => ({
              id: q.id,
              question: q.question || '',
              answer: q.answer || '',
              A2: q.A2 || null,
              A3: q.A3 || null,
              A4: q.A4 || null,
              A5: q.A5 || null,
              A6: q.A6 || null,
              dateAdded: q.dateAdded || '',
              uploadedAt: q.uploadedAt || null,
              lastReviewedDate: q.lastReviewedDate || null,
              interestRating: q.interestRating || null,
              difficulty: q.difficulty || null,
              genre: q.genre || '',
              questionType: q.questionType || '',
            })).sort((a, b) => {
              // Sort by most recently updated first
              const getTimestamp = (q) => {
                if (q.lastReviewedDate) {
                  const date = new Date(q.lastReviewedDate);
                  return isNaN(date.getTime()) ? 0 : date.getTime();
                }
                if (q.uploadedAt) {
                  const date = q.uploadedAt.toDate ? q.uploadedAt.toDate() : new Date(q.uploadedAt);
                  return isNaN(date.getTime()) ? 0 : date.getTime();
                }
                return 0;
              };
              return getTimestamp(b) - getTimestamp(a);
            })
          });
          totalExactDuplicates += questions.length - 1; // All except one are duplicates
        }
      });

      const uniqueQuestions = exactGroupsMap.size;
      const totalExactDuplicateGroups = exactDuplicateGroups.length;

      // Near duplicates detection (similarity > 0.5)
      // Process in chunks to keep UI responsive
      setProgress({ phase: 'Preparing questions...', percentage: 30, current: 0, total: allQuestions.length });
      
      // Optimization 1: Pre-normalize all questions once
      const normalizedQuestions = allQuestions.map(q => ({
        ...q,
        normalizedText: normalizeText(q.question || ''),
        textLength: (q.question || '').length
      })).filter(q => q.normalizedText); // Filter out empty questions
      
      const questionToGroup = new Map(); // question id -> group id
      const groups = new Map(); // group id -> Set of question ids
      let nextGroupId = 0;
      const similarPairs = [];
      
      // Process comparisons in chunks
      const CHUNK_SIZE = 200; // Optimization 4: Increased from 50 to 200
      const totalComparisons = (normalizedQuestions.length * (normalizedQuestions.length - 1)) / 2;
      let comparisonsDone = 0;
      let currentI = 0;
      let currentJ = 1;

      const processChunk = () => {
        if (cancelRef.current) {
          setError("Analysis cancelled by user");
          setLoading(false);
          return;
        }

        let chunkCount = 0;
        while (chunkCount < CHUNK_SIZE && currentI < normalizedQuestions.length) {
          if (currentJ >= normalizedQuestions.length) {
            currentI++;
            currentJ = currentI + 1;
            continue;
          }
          
          const q1 = normalizedQuestions[currentI];
          const text1 = q1.normalizedText;
          const len1 = q1.textLength;
          
          const q2 = normalizedQuestions[currentJ];
          const text2 = q2.normalizedText;
          const len2 = q2.textLength;
          
          // Optimization 2: Length-based early exit (fast filter)
          if (canBeSimilar(len1, len2, 0.5)) {
            // Only do expensive similarity calculation if lengths allow it
            const similarity = calculateSimilarity(text1, text2, 0.5);
            if (similarity > 0.5) {
              similarPairs.push({ id1: q1.id, id2: q2.id, similarity });
            }
          }
          
          comparisonsDone++;
          chunkCount++;
          currentJ++;
          
          if (currentJ >= normalizedQuestions.length) {
            currentI++;
            currentJ = currentI + 1;
          }
        }

        // Update progress
        const percentage = 30 + Math.floor((comparisonsDone / totalComparisons) * 60);
        setProgress({
          phase: `Comparing questions... (${comparisonsDone.toLocaleString()} / ${totalComparisons.toLocaleString()})`,
          percentage: Math.min(percentage, 90),
          current: comparisonsDone,
          total: totalComparisons
        });

        if (currentI < normalizedQuestions.length) {
          // Process next chunk after a short delay to keep UI responsive
          setTimeout(processChunk, 0);
        } else {
          // All comparisons done, now group them
          groupSimilarPairs();
        }
      };

      const groupSimilarPairs = () => {
        setProgress({ phase: 'Grouping similar questions...', percentage: 91, current: 0, total: similarPairs.length });
        
        // Group connected questions (if A is similar to B and B is similar to C, group A, B, C)
        similarPairs.forEach(({ id1, id2, similarity }, index) => {
          if (cancelRef.current) {
            setError("Analysis cancelled by user");
            setLoading(false);
            return;
          }

          const group1 = questionToGroup.get(id1);
          const group2 = questionToGroup.get(id2);

          if (!group1 && !group2) {
            // Create new group
            const newGroupId = nextGroupId++;
            groups.set(newGroupId, new Set([id1, id2]));
            questionToGroup.set(id1, newGroupId);
            questionToGroup.set(id2, newGroupId);
          } else if (group1 && !group2) {
            // Add id2 to group1
            groups.get(group1).add(id2);
            questionToGroup.set(id2, group1);
          } else if (!group1 && group2) {
            // Add id1 to group2
            groups.get(group2).add(id1);
            questionToGroup.set(id1, group2);
          } else if (group1 !== group2) {
            // Merge groups
            const group1Set = groups.get(group1);
            const group2Set = groups.get(group2);
            group2Set.forEach(id => {
              group1Set.add(id);
              questionToGroup.set(id, group1);
            });
            groups.delete(group2);
          }

          // Update progress every 100 pairs
          if (index % 100 === 0) {
            const percentage = 91 + Math.floor((index / similarPairs.length) * 4);
            setProgress({
              phase: 'Grouping similar questions...',
              percentage: Math.min(percentage, 95),
              current: index,
              total: similarPairs.length
            });
          }
        });
        
        buildNearDuplicateGroups();
      };

      const buildNearDuplicateGroups = () => {
        setProgress({ phase: 'Building results...', percentage: 96, current: 0, total: groups.size });
        
        // Build near-duplicate groups with question data
        const nearDuplicateGroups = [];
        let totalNearDuplicates = 0;
        let processedGroups = 0;

        groups.forEach((questionIds, groupId) => {
          if (cancelRef.current) {
            setError("Analysis cancelled by user");
            setLoading(false);
            return;
          }

          if (questionIds.size > 1) {
            const questionArray = Array.from(questionIds).map(id => 
              normalizedQuestions.find(q => q.id === id)
            ).filter(Boolean);

            // Calculate pairwise similarities for each question
            const questionsWithSimilarity = questionArray.map(q => {
              const similarities = questionArray
                .filter(other => other.id !== q.id)
                .map(other => {
                  // Use pre-normalized text and length-based filter
                  if (canBeSimilar(q.textLength, other.textLength, 0.5)) {
                    return calculateSimilarity(q.normalizedText, other.normalizedText, 0.5);
                  }
                  return 0;
                })
                .filter(sim => sim > 0);
              const avgSimilarity = similarities.length > 0
                ? similarities.reduce((a, b) => a + b, 0) / similarities.length
                : 0;

              return {
                id: q.id,
                question: q.question || '',
                answer: q.answer || '',
                dateAdded: q.dateAdded || '',
                interestRating: q.interestRating || null,
                difficulty: q.difficulty || null,
                genre: q.genre || '',
                questionType: q.questionType || '',
                similarity: avgSimilarity,
              };
            });

            const avgGroupSimilarity = questionsWithSimilarity.reduce((sum, q) => sum + q.similarity, 0) / questionsWithSimilarity.length;

            nearDuplicateGroups.push({
              id: groupId,
              similarity: avgGroupSimilarity,
              count: questionsWithSimilarity.length,
              questions: questionsWithSimilarity,
            });

            totalNearDuplicates += questionsWithSimilarity.length - 1;
          }
          
          processedGroups++;
          if (processedGroups % 10 === 0) {
            const percentage = 96 + Math.floor((processedGroups / groups.size) * 4);
            setProgress({
              phase: 'Building results...',
              percentage: Math.min(percentage, 99),
              current: processedGroups,
              total: groups.size
            });
          }
        });

        const totalNearDuplicateGroups = nearDuplicateGroups.length;

        setProgress({ phase: 'Complete!', percentage: 100, current: 0, total: 0 });

        setResults({
          totalQuestions,
          uniqueQuestions,
          exactDuplicateGroups,
          nearDuplicateGroups,
          totalExactDuplicates,
          totalNearDuplicates,
          totalExactDuplicateGroups,
          totalNearDuplicateGroups,
        });
        
        setLoading(false);
      };

      // Start processing
      processChunk();
    } catch (err) {
      console.error("Error analyzing duplicates:", err);
      setError(err.message || "Failed to analyze duplicates");
      setLoading(false);
    }
  }, []);

  const handleCancel = () => {
    cancelRef.current = true;
    setCancelled(true);
    setLoading(false);
    setError("Analysis cancelled by user");
  };

  // Check if user is Qwizard (same permission level as Library Viewer)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsQwizard(false);
        setCheckingRole(false);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        const isQwiz =
          userData.role === "Qwizard" ||
          user?.email === "rob@izeus.org" ||
          user?.email === "suelaw42@gmail.com";
        setIsQwizard(isQwiz);
        setCheckingRole(false);
      } catch (err) {
        console.error("Error checking user role:", err);
        setIsQwizard(false);
        setCheckingRole(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    // Only analyze duplicates if user has permission
    if (!checkingRole && isQwizard) {
      analyzeDuplicates();
    }
  }, [analyzeDuplicates, checkingRole, isQwizard]);

  const handleQuestionClick = (questionId) => {
    window.open(`/library-viewer?questionId=${questionId}`, '_blank');
  };

  // Helper to get most recent timestamp for a question
  const getMostRecentTimestamp = (q) => {
    if (q.lastReviewedDate) {
      const date = new Date(q.lastReviewedDate);
      if (!isNaN(date.getTime())) return date.getTime();
    }
    if (q.uploadedAt) {
      const date = q.uploadedAt.toDate ? q.uploadedAt.toDate() : new Date(q.uploadedAt);
      if (!isNaN(date.getTime())) return date.getTime();
    }
    return 0;
  };

  // Helper to format date/time
  const formatDateTime = (q) => {
    let date = null;
    if (q.lastReviewedDate) {
      date = new Date(q.lastReviewedDate);
    } else if (q.uploadedAt) {
      date = q.uploadedAt.toDate ? q.uploadedAt.toDate() : new Date(q.uploadedAt);
    }
    if (date && !isNaN(date.getTime())) {
      return date.toLocaleString();
    }
    return q.dateAdded || 'Unknown';
  };

  // Delete a question
  const handleDeleteQuestion = async (questionId, groupType, groupIndex) => {
    if (!window.confirm(`Are you sure you want to delete this question? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, "QuestionLibrary", questionId));
      
      // Remove from results
      setResults(prev => {
        if (groupType === 'exact') {
          const newGroups = [...prev.exactDuplicateGroups];
          newGroups[groupIndex] = {
            ...newGroups[groupIndex],
            questions: newGroups[groupIndex].questions.filter(q => q.id !== questionId),
            count: newGroups[groupIndex].count - 1
          };
          // Remove group if only one question left
          const filteredGroups = newGroups.filter(g => g.count > 1);
          return {
            ...prev,
            exactDuplicateGroups: filteredGroups,
            totalExactDuplicates: prev.totalExactDuplicates - 1,
            totalExactDuplicateGroups: filteredGroups.length
          };
        } else {
          const newGroups = [...prev.nearDuplicateGroups];
          newGroups[groupIndex] = {
            ...newGroups[groupIndex],
            questions: newGroups[groupIndex].questions.filter(q => q.id !== questionId),
            count: newGroups[groupIndex].count - 1
          };
          // Remove group if only one question left
          const filteredGroups = newGroups.filter(g => g.count > 1);
          return {
            ...prev,
            nearDuplicateGroups: filteredGroups,
            totalNearDuplicates: prev.totalNearDuplicates - 1,
            totalNearDuplicateGroups: filteredGroups.length
          };
        }
      });
    } catch (err) {
      console.error("Error deleting question:", err);
      alert(`Failed to delete question: ${err.message}`);
    }
  };

  // Show loading state while checking role
  if (checkingRole) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#223b4c",
          fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
          color: "#fff",
        }}
      >
        <div style={{ textAlign: "center", padding: 40 }}>
          <h2>Checking permissions...</h2>
        </div>
      </div>
    );
  }

  // Redirect if not Qwizard (same permission level as Library Viewer)
  if (!isQwizard) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#223b4c",
          fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
          color: "#fff",
        }}
      >
        <div style={{ textAlign: "center", padding: 40 }}>
          <h2 style={{ color: "#ff6b6b" }}>Access Denied</h2>
          <p>This page is only available to Qwizard administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Top Bar with Menu Button */}
      <div
        style={{
          width: "100%",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ffe066",
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          ☰ Menu
        </button>

        <div
          style={{
            marginLeft: 8,
            fontWeight: 900,
            color: "#f8faff",
          }}
        >
          Find Duplicates
        </div>
      </div>

      {/* Sidebar instance */}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      <div className="library-viewer-fullscreen">
        {loading ? (
          <div className="fullscreen-center" style={{ flexDirection: "column", gap: "20px" }}>
            <div style={{ fontSize: "1.2rem", marginBottom: "10px" }}>Analyzing duplicates...</div>
            <div style={{ width: "400px", maxWidth: "90vw" }}>
              <div style={{ color: "#aaa", marginBottom: "5px", fontSize: "0.9rem" }}>
                {progress.phase}
              </div>
              <div style={{ 
                width: "100%", 
                height: "24px", 
                background: "rgba(255,255,255,0.1)", 
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.2)"
              }}>
                <div style={{
                  width: `${progress.percentage}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #4CAF50, #45a049)",
                  transition: "width 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "0.85rem",
                  fontWeight: "bold"
                }}>
                  {progress.percentage}%
                </div>
              </div>
              {progress.total > 0 && (
                <div style={{ color: "#aaa", marginTop: "5px", fontSize: "0.85rem" }}>
                  {progress.current.toLocaleString()} / {progress.total.toLocaleString()}
                </div>
              )}
            </div>
            <button
              onClick={handleCancel}
              style={{
                marginTop: "20px",
                padding: "10px 20px",
                background: "#dc3545",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "1rem"
              }}
            >
              Cancel
            </button>
          </div>
        ) : error ? (
          <div className="fullscreen-center" style={{ flexDirection: "column", gap: "20px" }}>
            <div style={{ color: "#ff6b6b", fontSize: "1.2rem" }}>Error: {error}</div>
            <button
              onClick={analyzeDuplicates}
              style={{
                padding: "10px 20px",
                background: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "1rem"
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
            {/* Summary Statistics */}
            <div
              style={{
                background: "rgba(255,255,255,0.05)",
                borderRadius: 12,
                padding: "20px",
                marginBottom: "30px",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <h2 style={{ color: "#f8faff", marginTop: 0, marginBottom: "20px" }}>
                Summary Statistics
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "15px",
                }}
              >
                <div>
                  <div style={{ color: "#aaa", fontSize: "0.9rem" }}>Total Questions</div>
                  <div style={{ color: "#f8faff", fontSize: "1.5rem", fontWeight: "bold" }}>
                    {results.totalQuestions}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#aaa", fontSize: "0.9rem" }}>Unique Questions</div>
                  <div style={{ color: "#f8faff", fontSize: "1.5rem", fontWeight: "bold" }}>
                    {results.uniqueQuestions}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#aaa", fontSize: "0.9rem" }}>Exact Duplicate Groups</div>
                  <div style={{ color: "#ff6b6b", fontSize: "1.5rem", fontWeight: "bold" }}>
                    {results.totalExactDuplicateGroups}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#aaa", fontSize: "0.9rem" }}>Exact Duplicates</div>
                  <div style={{ color: "#ff6b6b", fontSize: "1.5rem", fontWeight: "bold" }}>
                    {results.totalExactDuplicates}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#aaa", fontSize: "0.9rem" }}>Near-Duplicate Groups</div>
                  <div style={{ color: "#ffa500", fontSize: "1.5rem", fontWeight: "bold" }}>
                    {results.totalNearDuplicateGroups}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#aaa", fontSize: "0.9rem" }}>Near-Duplicates</div>
                  <div style={{ color: "#ffa500", fontSize: "1.5rem", fontWeight: "bold" }}>
                    {results.totalNearDuplicates}
                  </div>
                </div>
              </div>
            </div>

            {/* Exact Duplicates Section */}
            {results.exactDuplicateGroups.length > 0 && (
              <div style={{ marginBottom: "40px" }}>
                <h2 style={{ color: "#f8faff", marginBottom: "20px" }}>
                  Exact Duplicates ({results.totalExactDuplicateGroups} groups)
                </h2>
                {results.exactDuplicateGroups.map((group, groupIndex) => (
                  <div
                    key={groupIndex}
                    style={{
                      background: "rgba(255,107,107,0.1)",
                      borderRadius: 12,
                      padding: "20px",
                      marginBottom: "20px",
                      border: "1px solid rgba(255,107,107,0.3)",
                    }}
                  >
                    <div style={{ marginBottom: "15px" }}>
                      <div style={{ color: "#ff6b6b", fontWeight: "bold", marginBottom: "5px" }}>
                        Group {groupIndex + 1} - {group.count} duplicates
                      </div>
                      <div style={{ color: "#aaa", fontSize: "0.9rem", fontStyle: "italic" }}>
                        "{group.normalizedText}"
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {group.questions.map((q, qIndex) => {
                        const isMostRecent = qIndex === 0; // First one is most recent after sorting
                        const allAnswers = [q.answer, q.A2, q.A3, q.A4, q.A5, q.A6].filter(a => a);
                        
                        return (
                          <div
                            key={q.id}
                            style={{
                              background: "rgba(0,0,0,0.2)",
                              borderRadius: 8,
                              padding: "15px",
                              border: isMostRecent 
                                ? "2px solid #4CAF50" 
                                : "1px solid rgba(255,255,255,0.1)",
                              position: "relative",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "10px" }}>
                              <div style={{ flex: 1, marginRight: "10px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "5px" }}>
                                  <div
                                    style={{
                                      color: "#ffe066",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                    }}
                                    onClick={() => handleQuestionClick(q.id)}
                                  >
                                    ID: {q.id}
                                  </div>
                                  {isMostRecent && (
                                    <div style={{
                                      background: "#4CAF50",
                                      color: "white",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                      marginLeft: "10px"
                                    }}>
                                      KEEP (Most Recent)
                                    </div>
                                  )}
                                  {!isMostRecent && (
                                    <div style={{
                                      background: "#ff6b6b",
                                      color: "white",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                      marginLeft: "10px"
                                    }}>
                                      DELETE
                                    </div>
                                  )}
                                </div>
                                <div style={{ color: "#f8faff", marginBottom: "8px" }}>
                                  <strong>Question:</strong> {q.question}
                                </div>
                                {allAnswers.length > 0 && (
                                  <div style={{ color: "#aaa", marginBottom: "8px" }}>
                                    <strong>Answers:</strong>
                                    <div style={{ marginLeft: "10px", marginTop: "4px" }}>
                                      {allAnswers.map((ans, idx) => (
                                        <div key={idx} style={{ marginBottom: "2px" }}>
                                          {ans}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div style={{ color: "#aaa", fontSize: "0.85rem", textAlign: "right", minWidth: "200px" }}>
                                <div style={{ marginBottom: "5px", fontWeight: "bold" }}>
                                  Last Updated: {formatDateTime(q)}
                                </div>
                                {q.dateAdded && <div>Date Added: {q.dateAdded}</div>}
                                {q.interestRating !== null && <div>Interest: {q.interestRating}</div>}
                                {q.difficulty !== null && <div>Difficulty: {q.difficulty}</div>}
                                {q.genre && <div>Genre: {q.genre}</div>}
                                {q.questionType && <div>Type: {q.questionType}</div>}
                              </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                              <button
                                onClick={() => handleDeleteQuestion(q.id, 'exact', groupIndex)}
                                style={{
                                  padding: "8px 16px",
                                  background: "#dc3545",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontWeight: "bold",
                                  fontSize: "0.9rem"
                                }}
                              >
                                Delete This Question
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Near Duplicates Section */}
            {results.nearDuplicateGroups.length > 0 && (
              <div style={{ marginBottom: "40px" }}>
                <h2 style={{ color: "#f8faff", marginBottom: "20px" }}>
                  Near Duplicates ({results.totalNearDuplicateGroups} groups, similarity &gt; 50%)
                </h2>
                {results.nearDuplicateGroups.map((group, groupIndex) => (
                  <div
                    key={group.id}
                    style={{
                      background: "rgba(255,165,0,0.1)",
                      borderRadius: 12,
                      padding: "20px",
                      marginBottom: "20px",
                      border: "1px solid rgba(255,165,0,0.3)",
                    }}
                  >
                    <div style={{ marginBottom: "15px" }}>
                      <div style={{ color: "#ffa500", fontWeight: "bold", marginBottom: "5px" }}>
                        Group {group.id + 1} - {group.count} near-duplicates
                      </div>
                      <div style={{ color: "#aaa", fontSize: "0.9rem" }}>
                        Average Similarity: {(group.similarity * 100).toFixed(1)}%
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {group.questions.map((q, qIndex) => {
                        const isMostRecent = qIndex === 0; // First one is most recent after sorting
                        const allAnswers = [q.answer, q.A2, q.A3, q.A4, q.A5, q.A6].filter(a => a);
                        
                        return (
                          <div
                            key={q.id}
                            style={{
                              background: "rgba(0,0,0,0.2)",
                              borderRadius: 8,
                              padding: "15px",
                              border: isMostRecent 
                                ? "2px solid #4CAF50" 
                                : "1px solid rgba(255,255,255,0.1)",
                              position: "relative",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "10px" }}>
                              <div style={{ flex: 1, marginRight: "10px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "5px" }}>
                                  <div
                                    style={{
                                      color: "#ffe066",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                    }}
                                    onClick={() => handleQuestionClick(q.id)}
                                  >
                                    ID: {q.id}
                                  </div>
                                  {isMostRecent && (
                                    <div style={{
                                      background: "#4CAF50",
                                      color: "white",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                      marginLeft: "10px"
                                    }}>
                                      KEEP (Most Recent)
                                    </div>
                                  )}
                                  {!isMostRecent && (
                                    <div style={{
                                      background: "#ff6b6b",
                                      color: "white",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                      marginLeft: "10px"
                                    }}>
                                      DELETE
                                    </div>
                                  )}
                                </div>
                                <div style={{ color: "#f8faff", marginBottom: "8px" }}>
                                  <strong>Question:</strong> {q.question}
                                </div>
                                {allAnswers.length > 0 && (
                                  <div style={{ color: "#aaa", marginBottom: "8px" }}>
                                    <strong>Answers:</strong>
                                    <div style={{ marginLeft: "10px", marginTop: "4px" }}>
                                      {allAnswers.map((ans, idx) => (
                                        <div key={idx} style={{ marginBottom: "2px" }}>
                                          {ans}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <div style={{ color: "#ffa500", fontSize: "0.9rem", marginTop: "5px" }}>
                                  Similarity: {(q.similarity * 100).toFixed(1)}%
                                </div>
                              </div>
                              <div style={{ color: "#aaa", fontSize: "0.85rem", textAlign: "right", minWidth: "200px" }}>
                                <div style={{ marginBottom: "5px", fontWeight: "bold" }}>
                                  Last Updated: {formatDateTime(q)}
                                </div>
                                {q.dateAdded && <div>Date Added: {q.dateAdded}</div>}
                                {q.interestRating !== null && <div>Interest: {q.interestRating}</div>}
                                {q.difficulty !== null && <div>Difficulty: {q.difficulty}</div>}
                                {q.genre && <div>Genre: {q.genre}</div>}
                                {q.questionType && <div>Type: {q.questionType}</div>}
                              </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                              <button
                                onClick={() => handleDeleteQuestion(q.id, 'near', groupIndex)}
                                style={{
                                  padding: "8px 16px",
                                  background: "#dc3545",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontWeight: "bold",
                                  fontSize: "0.9rem"
                                }}
                              >
                                Delete This Question
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {results.exactDuplicateGroups.length === 0 && results.nearDuplicateGroups.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px", color: "#aaa" }}>
                No duplicates found! All questions are unique.
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

